import { Player } from "./hand";
import { createDecks } from "./randUtil";
import { Stack } from "./stack";
import {
  Card,
  GameState,
  Message,
  Opcode,
  Pile,
  pileIndex,
  slotIndex,
  StatePlaying,
} from "./types";

// contains all the instructions of the abstract machine being the game state
export namespace Instruction {
  export function tryApply(
    state: GameState,
    action: Message,
    tick: number,
  ): Message | undefined {
    switch (action.kind) {
      case "uncover":
        if (state.gamePhase.kind != "normal") {
          return undefined;
        }
        if (tryUncover(indexToDeck(state, action.srcDeck)[1])) {
          return action;
        } else {
          return undefined;
        }
      case "select":
        if (state.gamePhase.kind != "normal") {
          return undefined;
        }
        if (
          trySelect(
            indexToSlot(state, action.slot)[1],
            (action.slot % 8) as slotIndex,
          )
        ) {
          return action;
        } else {
          return undefined;
        }
      case "moveStart":
        if (state.gamePhase.kind != "normal") {
          return undefined;
        }
        if (tryMoveStart(state, action.srcSlot, action.dstPile, tick)) {
          return action;
        } else {
          return undefined;
        }
      case "moveSuccess":
        {
          if (state.gamePhase.kind != "normal") {
            return undefined;
          }
          const movingCard = indexToMovingCard(state, action.srcSlot);
          if (movingCard) {
            if (tryMoveSuccess(state, movingCard)) {
              return action;
            } else {
              if (tryMoveFail(state, movingCard)) {
                return {
                  kind: "moveFail",
                  opcode: Opcode.MoveFail,
                  srcSlot: action.srcSlot,
                  dstPile: action.dstPile,
                };
              }
            }
          }
        }
        return undefined;
      case "moveFail": {
        if (state.gamePhase.kind != "normal") {
          return undefined;
        }
        const movingCard = indexToMovingCard(state, action.srcSlot);
        if (movingCard) {
          if (tryMoveFail(state, movingCard)) {
            return action;
          }
        }
        return undefined;
      }
      case "stress":
        stress(state, tick);
        return action;
      case "stressResult":
        state.gamePhase = {
          kind: "stress",
          startTick: tick,
          winnerId: action.winnerId,
        };
        return action;
      case "endStress":
        if (state.gamePhase.kind != "stress") {
          return undefined;
        }
        stressMovePiles(
          state,
          state.gamePhase.winnerId === 0 ? state.playerZero : state.playerOne,
        );
        state.gamePhase = { kind: "normal" };
        return action;
      case "stuck":
        if (state.gamePhase.kind != "normal") {
          return undefined;
        }
        state.gamePhase = { kind: "stuck", startTick: tick };
        return action;
      case "endStuck":
        if (state.gamePhase.kind != "stuck") {
          return undefined;
        }
        stuck(state);
        state.gamePhase = { kind: "normal" };
        return action;
      case "seedState":
        if (trySeedState(state, action.seed)) {
          return action;
        }
        return undefined;
      case "synchronize":
      case "start":
        return action;
      case "winner":
        state.winnerId = action.playerId;
        return action;
    }
  }

  function indexToMovingCard(
    state: GameState,
    srcSlot: slotIndex,
  ): StatePlaying | undefined {
    const [_, cards] = indexToSlot(state, srcSlot);
    if (cards.state.kind === "playing") {
      return cards.state;
    }
    return undefined;
  }

  // tries to seed a game
  export function trySeedState(state: GameState, seed: number): boolean {
    const [playerZero, playerOne] = createDecks(seed);

    // Pull initial middle cards from the players' decks to start
    const leftStart = playerZero.pop()!;
    const rightStart = playerOne.pop()!;

    state.playerZero = new Player(playerZero);
    state.playerOne = new Player(playerOne);
    state.leftPile = new Stack<number>(leftStart);
    state.rightPile = new Stack<number>(rightStart);
    state.sidePile = new Stack<number>(...[]);
    state.gamePhase = { kind: "normal" };
    console.log(`seeded game with: ${seed}\n result: ${JSON.stringify(state)}`);
    return true;
  }

  // uncovers a card from the deck and places in the players hand
  export function tryUncover(cards: Player): boolean {
    if (cards.state.kind === "playing") {
      return false;
    }
    cards.hand = cards.hand.filter((s: number[]) => {
      return s.length > 0;
    });
    if (cards.hand.length < 4 && cards.deck.length > 0) {
      const topCard: number = cards.deck.pop()!;
      let placed = false;
      for (let i = 0; i < cards.hand.length; i++) {
        if (Card.rank(topCard) < Card.rank(cards.hand[i][0])) {
          cards.hand.splice(i, 0, [topCard]);
          placed = true;
          break;
        }
        if (Card.rank(cards.hand[i][0]) === Card.rank(topCard)) {
          cards.hand[i].push(topCard);
          placed = true;
          break;
        }
      }
      if (!placed) {
        cards.hand.push([topCard]);
      }
      cards.state = { kind: "idle" };
      return true;
    }
    return false;
  }

  // start a move from a slot to a pile this disables all further actions
  // until the move is either completed or fails.
  export function tryMoveStart(
    state: GameState,
    src: slotIndex,
    dest: pileIndex,
    tick: number,
  ): boolean {
    const dstPile = indexToPile(state, dest);
    const [srcSlot, cards] = indexToSlot(state, src);

    // other action in progress
    if (
      srcSlot.length === 0 ||
      dstPile.isEmpty() ||
      cards.state.kind !== "selected"
    ) {
      return false;
    }
    if (!isValidMove(srcSlot[srcSlot.length - 1], dstPile.peek()!)) {
      return false;
    }
    const toPlay: number = srcSlot.pop()!;
    cards.state = {
      kind: "playing",
      card: toPlay,
      srcSlot: src,
      dstPile: dest,
      startTick: tick,
    };
    return true;
  }

  // Tries to play the move, returns true if it succeeds.
  export function tryMoveSuccess(s: GameState, move: StatePlaying): boolean {
    const dst = indexToPile(s, move.dstPile);
    const cards = move.srcSlot < 4 ? s.playerZero : s.playerOne;
    if (!isValidMove(move.card, dst.peek()!)) {
      return false;
    }
    dst.push(move.card);

    cards.state = { kind: "idle" };
    return true;
  }

  // Puts the unplayable card back into the players hand
  export function tryMoveFail(s: GameState, move: StatePlaying): boolean {
    const cards = move.srcSlot < 4 ? s.playerZero : s.playerOne;
    cards.hand[move.srcSlot % 8].push(move.card);
    cards.state = { kind: "idle" };
    return true;
  }

  export function stress(s: GameState, startTick: number): void {
    s.gamePhase = {
      kind: "stress",
      startTick,
      winnerId: undefined,
    };
  }

  // selects a card in the players hand, this card can be
  // played next
  export function trySelect(cards: Player, slot: slotIndex): boolean {
    if (cards.state.kind === "playing") {
      return false;
    }
    cards.state = { kind: "selected", slot };
    return true;
  }

  // moves middle piles to side
  export function stuck(s: GameState): void {
    drainToDeck(s.sidePile, s.leftPile);
    drainToDeck(s.sidePile, s.rightPile);
    repopulatePiles(s);
  }

  // moves all playing piles + side piles to the losers deck
  export function stressMovePiles(s: GameState, loser: Player): void {
    drainToDeck(loser.deck, s.leftPile);
    drainToDeck(loser.deck, s.rightPile);
    drainToDeck(loser.deck, s.sidePile);
    repopulatePiles(s);
  }

  // ------- HELPERS ------
  // Valid Move: Rank is +/- 1 (wrapping Ace and King)
  function isValidMove(source: number, target: number): boolean {
    const diff = Math.abs(Card.rank(source) - Card.rank(target));
    return diff === 1 || diff === 12;
  }

  // adds all cards in cards to this players deck/hand and consumes them
  function drainToDeck(dstDeck: Pile | number[], pile: Pile) {
    while (pile.length > 0) {
      dstDeck.push(pile.pop()!);
    }
  }

  // repopulates the middle piles by either drawing a card from the players
  // or from the side pile
  function repopulatePiles(s: GameState): void {
    if (s.playerZero.deck.length > 0) {
      s.leftPile.push(s.playerZero.deck.pop()!);
    } else {
      s.leftPile.push(s.sidePile.pop()!);
    }
    if (s.playerOne.deck.length > 0) {
      s.rightPile.push(s.playerOne.deck.pop()!);
    } else {
      s.rightPile.push(s.sidePile.pop()!);
    }
  }

  function indexToSlot(s: GameState, index: number): [number[], Player] {
    switch (index) {
      case 0:
      case 1:
      case 2:
      case 3:
        return [s.playerZero.hand[index], s.playerZero];
      case 8:
      case 9:
      case 10:
      case 11:
        return [s.playerOne.hand[index % 8], s.playerOne];
      default:
        throw new Error("Invalid index for slot: " + index);
    }
  }

  function indexToDeck(s: GameState, index: number): [number[], Player] {
    switch (index) {
      case 4:
        return [s.playerZero.deck, s.playerZero];
      case 12:
        return [s.playerOne.deck, s.playerOne];
      default:
        throw new Error("Invalid index for deck: " + index);
    }
  }

  function indexToPile(s: GameState, index: number): Pile {
    switch (index) {
      case 5:
        return s.leftPile;
      case 6:
        return s.rightPile;
      default:
        throw new Error("Invalid index for pile/slot: " + index);
    }
  }
}
